import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { initialState } from './initialState';
import { PAYMENT_BILL_TYPE } from '../constants';
import type { EditableManualBill, EditableOutstandingBill } from '../types/bills.types';
import { splitOutstandingBills } from '../utils';

const billsSlice = createSlice({
  initialState,
  name: 'Bills',
  reducers: {
    addManualBills(state, action: PayloadAction<EditableManualBill & { mid: string }>) {
      const { mid, ...bill } = action.payload;
      const { AmountDueFromPatient, BillType } = bill;

      // If the bill is being added under a different mid, reset the manual bills to the initial state
      if (state.manualBillsMid !== undefined && state.manualBillsMid !== mid) {
        state.manualBills = [...initialState.manualBills];
      }

      state.manualBills.push({
        ...bill,
        isFullyPaidBill:
          BillType === PAYMENT_BILL_TYPE.MANUAL_USER ||
          (AmountDueFromPatient <= 0 && BillType === PAYMENT_BILL_TYPE.MANUAL),
      });
      state.manualBillsMid = mid;
    },
    deleteManualBill(state, action: PayloadAction<{ invoiceNumber: string }>) {
      state.manualBills = state.manualBills.filter((record) => action.payload.invoiceNumber !== record.InvoiceNumber);
    },
    resetStatePayments: (state) => {
      state.outstandingBills = initialState.outstandingBills;
      state.manualBills = initialState.manualBills;
      state.manualBillsMid = initialState.manualBillsMid;
    },
    resetManualBills: (state) => {
      state.manualBills = initialState.manualBills;
      state.manualBillsMid = initialState.manualBillsMid;
    },
    setCheckAllOutstandingBills(state, action: PayloadAction<boolean>) {
      state.outstandingBills.outstanding.forEach((record) => {
        record.isChecked = action.payload;
      });
    },
    setOutstandingBills(state, action: PayloadAction<EditableOutstandingBill[]>) {
      const { outstanding, pending } = splitOutstandingBills(action.payload);

      state.outstandingBills.outstanding = outstanding.map((record) => {
        const matchExistingBill = state.outstandingBills.outstanding.find(
          (b) => b.InvoiceNumber === record.InvoiceNumber
        );

        if (matchExistingBill) {
          return {
            ...matchExistingBill,
            ...record,
            isChecked: matchExistingBill.isChecked,
            updatedAmount: matchExistingBill.updatedAmount,
          };
        }

        return {
          ...record,
          isChecked: true,
          updatedAmount: record.AmountDueFromPatient > 0 ? record.AmountDueFromPatient : 0,
        };
      });

      state.outstandingBills.pending = pending.map((p) => ({
        ...p,
        isChecked: false,
      }));
    },
    updateManualBill(state, action: PayloadAction<{ data: Partial<EditableManualBill>; id: string }>) {
      const { data, id } = action.payload;

      state.manualBills.forEach((record) => {
        if (record.InvoiceNumber === id) {
          Object.assign(record, data);
        }
      });
    },
    updateOutstandingBill(state, action: PayloadAction<{ data: Partial<EditableOutstandingBill>; id: string }>) {
      const { data, id } = action.payload;

      state.outstandingBills.outstanding.forEach((record) => {
        if (record.InvoiceNumber === id) {
          Object.assign(record, data);
        }
      });
    },
    validateEditAmount: (state, action: PayloadAction<boolean>) => {
      state.isValidEditAmount = action.payload;
    },
  },
});

export const { actions: billsAction } = billsSlice;

export default billsSlice.reducer;
